import {
  Button,
  type CSSVariablesResolver,
  type MantineSize,
  type MantineTheme,
  type MantineThemeOverride,
  Tabs,
  type TitleProps,
  rem,
} from '@mantine/core';
import tabsStyles from './tabs.module.sass';
import switchStyles from './switch.module.sass';
import formStyles from './form.module.sass';
import styles from './exports.module.scss';
import {
  CONTROL_SIZES,
  CONTROL_SIZES_COMPACT,
  PAGINATION_SIZES,
  getButtonFontSize,
  getScaledValue,
  variantColorResolver,
} from '@/utils/theme';
import {
  paletteAqua,
  paletteBrown,
  paletteColorBase,
  paletteColorBrand,
  paletteColorCalendar,
  paletteColorCalendarBg,
  paletteColorFail,
  paletteColorSuccess,
  paletteColorWarn,
  paletteElectric,
  paletteFuchsia,
  paletteLavender,
  paletteLime,
  paletteMinistry,
  paletteMoss,
  paletteYellow,
} from './palette';
import { type ExtendedCustomColors, WEB_PARTITION } from '@/types';
import { FORMAT_EDIT_DATE } from '@/constants';

const DEFAULT_SIZE: MantineSize = 'md';

const CALCULATED_CONTROL_SIZE = CONTROL_SIZES.md;

export const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    '--mantine-shadow-xs': theme.shadows.xs,
    '--mantine-shadow-xxs': theme.shadows.xxs,
    '--mantine-shadow-sm': theme.shadows.sm,
    '--mantine-shadow-md': theme.shadows.md,
    '--mantine-shadow-lg': theme.shadows.lg,
    '--mantine-shadow-xl': theme.shadows.xl,
  },
  light: {
    '--mantine-color-text': paletteColorBase[9],
  },
  dark: {},
});

export const ALL_COLORS: ExtendedCustomColors[] = ['brand', 'base', 'success', 'fail', 'warn'];

export const theme: MantineThemeOverride = {
  primaryColor: 'brand',
  fontFamily: 'Lato, sans-serif',
  variantColorResolver,
  colors: {
    brand: paletteColorBrand,
    fail: paletteColorFail,
    base: paletteColorBase,
    success: paletteColorSuccess,
    warn: paletteColorWarn,
    calendar: paletteColorCalendar,
    calendarEvents: paletteColorCalendarBg,
    aqua: paletteAqua,
    brown: paletteBrown,
    electric: paletteElectric,
    fuchsia: paletteFuchsia,
    lavender: paletteLavender,
    lime: paletteLime,
    ministry: paletteMinistry,
    moss: paletteMoss,
    yellow: paletteYellow,
  },
  radius: {
    xs: '2px',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
  },

  shadows: {
    xs: '0px 1px 2px 0px rgba(17, 23, 39, 0.05)',
    xxs: '0px 1px 4px 0px #1117270D, 0px 1px 4px 0px #1117271A',
    sm: `
      0px 1px 3px 0px rgba(17, 23, 39, 0.051),
      0px 10px 15px -5px rgba(17, 23, 39, 0.102),
      0px 7px 7px -5px rgba(17, 23, 39, 0.0392)
    `,
    md: `
    0px 1px 3px 0px rgba(17, 23, 39, 0.05),
    0px 20px 25px -5px rgba(17, 23, 39, 0.1),
    0px 10px 10px -5px rgba(17, 23, 39, 0.04),
    `,
    lg: '0px 10px 15px -3px rgba(17, 23, 39, 0.1), 0px 4px 6px -2px rgba(17, 23, 39, 0.05)',
    xl: '0px 20px 25px -5px rgba(17, 23, 39, 0.1), 0px 10px 10px -5px rgba(17, 23, 39, 0.04)',
  },

  headings: {
    fontFamily: styles.fontFamilyTitle,
    sizes: {
      h1: { fontSize: '1.5rem', lineHeight: '1.334', fontWeight: '500' },
      h2: { fontSize: '1.25rem', lineHeight: '1.5', fontWeight: '500' },
      h3: { fontSize: '1rem', lineHeight: '1.5', fontWeight: '700' },
      h4: { fontSize: '1.5rem', lineHeight: '1.4' },
      h5: { fontSize: '1.25rem', lineHeight: '1.45' },
      h6: { fontSize: '1rem', lineHeight: '1.5' },
    },
  },
  components: {
    Slider: {
      styles: {
        bar: {
          backgroundColor: 'var(--mantine-color-brand-6)',
        },
        thumb: {
          borderColor: 'var(--mantine-color-brand-6)',
        },
      },
    },
    AppShell: {
      styles: {
        root: {
          backgroundColor: styles.colorBg,
        },
      },
    },
    Avatar: {
      defaultProps: {
        size: DEFAULT_SIZE,
      },
      styles: {
        root: {
          '--avatar-size-xs': CONTROL_SIZES.xs,
          '--avatar-size-sm': CONTROL_SIZES.sm,
          '--avatar-size-md': CONTROL_SIZES.md,
          '--avatar-size-lg': CONTROL_SIZES.lg,
          '--avatar-size-xl': CONTROL_SIZES.xl,
        },
      },
    },
    TextInput: {
      classNames: formStyles,
      styles: {
        wrapper: {
          '--input-height-xs': CONTROL_SIZES.xs,
          '--input-height-sm': CONTROL_SIZES.sm,
          '--input-height-md': CONTROL_SIZES.md,
          '--input-height-lg': CONTROL_SIZES.lg,
          '--input-height-xl': CONTROL_SIZES.xl,
        },
      },
    },
    Input: {
      classNames: formStyles,
      styles: {
        wrapper: {
          '--input-height-xs': CONTROL_SIZES.xs,
          '--input-height-sm': CONTROL_SIZES.sm,
          '--input-height-md': CONTROL_SIZES.md,
          '--input-height-lg': CONTROL_SIZES.lg,
          '--input-height-xl': CONTROL_SIZES.xl,
        },
      },
    },
    InputWrapper: {
      classNames: formStyles,
    },
    InputBase: {
      classNames: formStyles,
      styles: (_: unknown, params: { color?: string }) => {
        const borderColor = params.color ? `var(--mantine-color-${params.color}-6)` : undefined;
        const textColor = params.color ? `var(--mantine-color-${params.color}-6)` : undefined;

        return {
          input: {
            borderColor,
            color: textColor,
          },
          section: {
            color: textColor,
          },
        };
      },
    },
    Textarea: {
      classNames: formStyles,
      styles: {
        wrapper: {
          '--input-height-xs': CONTROL_SIZES.xs,
          '--input-height-sm': CONTROL_SIZES.sm,
          '--input-height-md': CONTROL_SIZES.md,
          '--input-height-lg': CONTROL_SIZES.lg,
          '--input-height-xl': CONTROL_SIZES.xl,
        },
      },
    },
    MultiSelect: {
      styles: {
        pill: {
          backgroundColor: 'transparent',
          border: `1px solid ${paletteColorBase[3]}`,
          color: paletteColorBase[7],
        },

        pillRemove: {
          color: 'var(--mantine-color-brand-6)',
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'var(--mantine-color-brand-7)',
          },
        },

        // Контейнер списка выбранных значений
        pillsList: {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '12px',
        },
      },
    },
    Checkbox: {
      defaultProps: {
        size: DEFAULT_SIZE,
      },
      styles: (_: MantineTheme, params: { disabled?: boolean }) => ({
        root: {
          '--checkbox-size-xs': styles.checkboxSizeXS,
          '--checkbox-size-sm': styles.checkboxSizeSM,
          '--checkbox-size-md': styles.checkboxSizeMD,
          '--checkbox-size-lg': styles.checkboxSizeLG,
          '--checkbox-size-xl': styles.checkboxSizeXL,
          '--checkbox-radius': getScaledValue('0.25rem'), // 4px
          cursor: params.disabled ? 'not-allowed' : 'pointer',
        },
        input: {
          cursor: params.disabled ? 'not-allowed' : 'pointer',
        },
      }),
    },
    Button: Button.extend({
      defaultProps: {
        size: DEFAULT_SIZE,
      },
      vars: (_, props) => {
        const { size = DEFAULT_SIZE, variant = 'filled', color = 'brand' } = props;

        // Calculate hover color based on variant and color
        let hoverColor = 'var(--button-hover)';

        if (variant === 'outline') {
          if (color === 'brand') {
            hoverColor = 'var(--mantine-color-brand-0)';
          } else if (color === 'success') {
            hoverColor = 'var(--mantine-color-success-0)';
          } else if (color === 'fail') {
            hoverColor = 'var(--mantine-color-fail-0)';
          } else if (color === 'warn') {
            hoverColor = 'var(--mantine-color-warn-0)';
          } else if (color === 'base') {
            hoverColor = 'var(--mantine-color-base-0)';
          }
        }

        return {
          root: {
            '--button-fz': getButtonFontSize(size as MantineSize),
            '--button-hover-override': hoverColor,
          },
        };
      },
      styles: {
        root: {
          fontWeight: styles.fontWeightMedium,
          '--button-height-xs': CONTROL_SIZES.xs,
          '--button-height-sm': CONTROL_SIZES.sm,
          '--button-height-md': CONTROL_SIZES.md,
          '--button-height-lg': CONTROL_SIZES.lg,
          '--button-height-xl': CONTROL_SIZES.xl,
          '--button-height-compact-sm': CONTROL_SIZES_COMPACT.sm,
          '--button-height-compact-md': CONTROL_SIZES_COMPACT.md,
          '--button-height-compact-lg': CONTROL_SIZES_COMPACT.lg,
          '--button-height-compact-xl': CONTROL_SIZES_COMPACT.xl,
          '--button-padding-x': getScaledValue('1rem'),
          '--button-padding-x-xs': getScaledValue('1rem'),
          '--button-padding-x-sm': getScaledValue('1rem'),
          '--button-padding-x-md': getScaledValue('1rem'),
          '--button-padding-x-lg': getScaledValue('1rem'),
          '--button-padding-x-xl': getScaledValue('1rem'),
          '--button-padding-x-compact-xs': getScaledValue('1rem'),
          '--button-padding-x-compact-sm': getScaledValue('1rem'),
          '--button-padding-x-compact-md': getScaledValue('1rem'),
          '--button-padding-x-compact-lg': getScaledValue('1rem'),
          '--button-padding-x-compact-xl': getScaledValue('1rem'),
          '--button-radius': 'var(--mantine-radius-xl)',
          '&:hover:not(:disabled)': {
            backgroundColor: 'var(--button-hover-override, var(--button-hover)) !important',
          },
        },
      },
    }),
    Tabs: Tabs.extend({
      classNames: tabsStyles,
    }),
    Title: {
      styles: (theme: MantineTheme, params: TitleProps) => ({
        root: {
          fontWeight: 600,
          fontFamily: styles.fontFamilyAccent,
          color: theme.colors.base[10],
          marginBottom: rem(8),

          ...(params.order === 1 && {
            fontSize: rem(72),
            lineHeight: rem(90),
          }),
          ...(params.order === 2 && {
            fontSize: rem(60),
            lineHeight: rem(72),
          }),
          ...(params.order === 3 && {
            fontSize: rem(48),
            lineHeight: rem(60),
          }),
          ...(params.order === 4 && {
            fontSize: rem(36),
            lineHeight: rem(44),
          }),
          ...(params.order === 5 && {
            fontSize: rem(30),
            lineHeight: rem(38),
          }),
          ...(params.order === 6 && {
            fontSize: rem(24),
            lineHeight: rem(32),
            fontWeight: styles.fontWeightMedium,
          }),
        },
      }),
    },
    Badge: {
      styles: {
        root: {
          margin: 0,
        },
      },
    },
    Modal: {
      styles: {
        inner: {
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        title: {
          fontSize: '1.125rem', // 18px
          fontWeight: styles.fontWeightSemibold,
          flex: 1,
        },
        close: {
          '&:focus': {
            outline: 'none',
          },
        },
      },
    },
    ActionIcon: {
      defaultProps: {
        size: '',
      },
      vars: (_: unknown, props: { variant?: string; color?: string }) => {
        const { variant = 'filled', color = 'brand' } = props;

        // Calculate hover color based on variant and color
        let hoverColor = 'var(--ai-hover)';

        if (variant === 'outline') {
          if (color === 'brand') {
            hoverColor = 'var(--mantine-color-brand-0)';
          } else if (color === 'success') {
            hoverColor = 'var(--mantine-color-success-0)';
          } else if (color === 'fail') {
            hoverColor = 'var(--mantine-color-fail-0)';
          } else if (color === 'warn') {
            hoverColor = 'var(--mantine-color-warn-0)';
          } else if (color === 'base') {
            hoverColor = 'var(--mantine-color-base-0)';
          }
        } else if (variant === 'light') {
          if (color === 'brand') {
            hoverColor = 'var(--mantine-color-brand-1)';
          } else if (color === 'success') {
            hoverColor = 'var(--mantine-color-success-1)';
          } else if (color === 'fail') {
            hoverColor = 'var(--mantine-color-fail-1)';
          } else if (color === 'warn') {
            hoverColor = 'var(--mantine-color-warn-1)';
          } else if (color === 'base') {
            hoverColor = 'var(--mantine-color-base-1)';
          }
        }

        return {
          root: {
            '--ai-hover-override': hoverColor,
            '--mantine-color-disabled-color': paletteColorBase[4],
          },
        };
      },
      styles: {
        root: {
          '--ai-size-xs': CONTROL_SIZES.xs,
          '--ai-size-sm': CONTROL_SIZES.sm,
          '--ai-size-md': CONTROL_SIZES.md,
          '--ai-size-lg': CONTROL_SIZES.lg,
          '--ai-size-xl': CALCULATED_CONTROL_SIZE,
          '&:hover:not(:disabled)': {
            backgroundColor: 'var(--ai-hover-override, var(--ai-hover)) !important',
          },
        },
      },
    },
    Switch: {
      classNames: switchStyles,
    },
    FileInput: {
      styles: {
        input: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        },
        placeholder: {
          color: paletteColorBase[6],
        },
      },
    },
    Menu: {
      styles: (theme: MantineTheme) => ({
        dropdown: {
          boxShadow: theme.shadows.md,
          borderColor: paletteColorBase[3],
        },
        item: {
          padding: `${getScaledValue('0.5rem')} ${getScaledValue('0.75rem')}`, // 16px 12px
        },
      }),
    },
    DatePickerInput: {
      defaultProps: {
        valueFormat: FORMAT_EDIT_DATE,
        placeholder: FORMAT_EDIT_DATE,
      },
    },
    Table: {
      styles: {
        table: {
          border: 'none',
          borderCollapse: 'collapse',
          '--table-border-color': 'transparent',
        },
        thead: {
          backgroundColor: paletteColorBase[1],
        },
        tbody: {
          backgroundColor: '#fff',
        },
        th: {
          fontWeight: 'bold',
          fontSize: '0.875rem', // 14px
          padding: '0.75rem',
          height: '2.75rem',
          borderBottom: 'none',
          borderInlineStart: 'none',
          borderInlineEnd: 'none',
        },
        td: {
          padding: '0.75rem',
          height: '2.75rem',
          borderBottom: 'none',
          borderInlineStart: 'none',
          borderInlineEnd: 'none',
        },
        tr: {
          borderBottom: 'none',
        },
      },
    },
    Text: {
      defaultProps: {
        size: DEFAULT_SIZE,
      },
      styles: (_: unknown, params: { size: MantineSize | '2xl' }) => {
        const currentSize = params.size || 'md';

        let fontSize;
        let lineHeight;

        switch (currentSize) {
          case 'xs':
            fontSize = rem(12);
            lineHeight = rem(18);
            break;

          case 'sm':
            fontSize = rem(14);
            lineHeight = rem(20);
            break;

          case 'md':
            fontSize = rem(16);
            lineHeight = rem(24);
            break;

          case 'lg':
            fontSize = rem(18);
            lineHeight = rem(28);
            break;

          case 'xl':
            fontSize = rem(20);
            lineHeight = rem(30);
            break;
          case '2xl':
            fontSize = rem(24);
            lineHeight = rem(30);
            break;

          default:
            fontSize = '1rem';
            lineHeight = '1.5';
        }

        return {
          root: {
            fontSize,
            lineHeight,
          },
        };
      },
    },
    Pagination: {
      defaultProps: {
        size: DEFAULT_SIZE,
      },
      styles: (_: unknown, params: { size: MantineSize }) => {
        const paddingMap = {
          xs: getScaledValue('0.75rem'),
          sm: getScaledValue('0.75rem'),
          md: getScaledValue('0.75rem'),
          lg: getScaledValue('1rem'),
          xl: getScaledValue('1rem'),
        };

        const currentSize: string = PAGINATION_SIZES[params.size || 'md'];
        const currentPadding: string = paddingMap[params.size || 'md'];

        return {
          control: {
            height: currentSize,
            minWidth: currentSize,
            fontSize: '1rem',
            paddingLeft: currentPadding,
            paddingRight: currentPadding,
          },
          icon: {
            width: '1rem',
            height: '1rem',
          },
          controlIcon: {
            width: '1rem',
            height: '1rem',
          },
        };
      },
    },
    ColorPicker: {
      styles: {
        root: {},
      },
    },
    ColorSwatch: {
      vars: () => ({
        root: {
          '--cs-radius': '50%',
        },
      }),
    },
    Notification: {
      styles: (theme: MantineTheme, params: { variant?: string }) => {
        if (params.variant === 'gray') {
          return {
            root: {
              backgroundColor: 'var(--mantine-color-gray-1)',
              color: theme.colors.base[9],
            },
            icon: {
              backgroundColor: 'transparent',
            },
          };
        }
        return {};
      },
    },
  },
  defaultRadius: DEFAULT_SIZE,
  spacing: {
    xs: styles.spacingXS,
    sm: styles.spacingSM,
    md: styles.spacingMD,
    lg: styles.spacingLG,
    xl: styles.spacingXL,
    xxl: styles.spacingXXL,
    basic: styles.spacingBasic,
  },
  breakpoints: {
    xs: styles.breakpointXS,
    sm: styles.breakpointSM,
    md: styles.breakpointMD,
    lg: styles.breakpointLG,
    xl: styles.breakpointXL,
  },
};

export const getThemeForPartition = ({ partition }: { partition: WEB_PARTITION }): MantineThemeOverride => {
  if (partition !== WEB_PARTITION.MINISTRIES) {
    return theme;
  }

  return {
    ...theme,
    colors: {
      ...theme.colors,
      brand: paletteMinistry,
    },
  };
};
